import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Target, Eye, Lightbulb, Users, MapPin } from "lucide-react";

const values = [
  { icon: Target, title: "Mission-Driven", desc: "We build technology and strategies that create measurable business impact for every client we serve." },
  { icon: Eye, title: "Transparent Process", desc: "From discovery to deployment, clients have full visibility into timelines, budgets, and deliverables." },
  { icon: Lightbulb, title: "Innovation First", desc: "We adopt cutting-edge frameworks and methodologies to keep our clients ahead of the curve." },
  { icon: Users, title: "Client-Centric", desc: "Every engagement is tailored. We don't do cookie-cutter — we engineer bespoke solutions." },
];

export default function AboutUs() {
  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="mb-16" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Badge variant="outline" className="mb-4 border-[#00F2FE]/30 text-[#00F2FE] bg-[#00F2FE]/5 px-3 py-1 text-xs tracking-wider uppercase">
            Who We Are
          </Badge>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-50 mb-6">
            About <span className="gradient-text">Devkuba Ventures</span>
          </h1>
          <p className="text-slate-400 text-sm sm:text-base max-w-3xl leading-relaxed">
            Devkuba Ventures Private Limited is a premium IT Services &amp; Growth Marketing agency headquartered in Ahmedabad, Gujarat. We partner with startups, enterprises, and government bodies to deliver high-performance digital products, scalable software architectures, and data-driven marketing campaigns that accelerate growth.
          </p>
        </motion.div>

        {/* Story Section */}
        <motion.div
          className="bg-[#0F172A] border border-white/[0.06] rounded-xl p-8 sm:p-10 mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-slate-100 mb-4">Our Story</h2>
          <p className="text-slate-400 text-sm sm:text-base leading-relaxed mb-4">
            Founded with a vision to bridge the gap between cutting-edge technology and real business outcomes, Devkuba Ventures has grown into a trusted partner for companies across industries — from fintech and e-commerce to government infrastructure and lifestyle brands.
          </p>
          <p className="text-slate-400 text-sm sm:text-base leading-relaxed">
            Our team combines deep engineering expertise with creative marketing acumen, enabling us to take projects from ideation through launch and beyond. We've delivered 120+ projects, generated over ₹45 Cr in client revenue, and maintain a 98.4% client satisfaction rate.
          </p>
        </motion.div>

        {/* Values Grid */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-slate-100 mb-8">What Drives Us</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {values.map((v, i) => (
              <motion.div
                key={v.title}
                className="bg-[#0F172A] border border-white/[0.06] rounded-xl p-6 hover:border-[#00F2FE]/20 transition-colors"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + i * 0.08 }}
              >
                <div className="w-10 h-10 rounded-lg bg-[#00F2FE]/10 border border-[#00F2FE]/20 flex items-center justify-center mb-4">
                  <v.icon className="h-5 w-5 text-[#00F2FE]" />
                </div>
                <h3 className="font-heading font-semibold text-slate-100 mb-2">{v.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Location */}
        <motion.div
          className="mt-12 bg-[#0F172A] border border-white/[0.06] rounded-xl p-8 flex items-start gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <MapPin className="h-6 w-6 text-[#0EA5E9] mt-0.5 shrink-0" />
          <div>
            <h3 className="font-heading font-semibold text-slate-100 mb-1">Our Office</h3>
            <p className="text-slate-400 text-sm leading-relaxed">
              SCO - 101, Suramya Heights, Eklingji Road, Sanand, Ahmedabad, Gujarat, India - 382110
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
